import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import DictationInput from '../components/DictationInput';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Wallet, Upload, FileText, Shield, Award, Briefcase, Home,
  Heart, GraduationCap, Scale, Plus, Trash2, Eye, Download,
  RefreshCw, AlertCircle, Calendar, Building
} from 'lucide-react';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';

const ICON_MAP = {
  'id-card': Shield,
  'shield': Shield,
  'file-text': FileText,
  'file': FileText,
  'award': Award,
  'briefcase': Briefcase,
  'home': Home,
  'heart': Heart,
  'scale': Scale,
  'graduation-cap': GraduationCap,
};

export default function ResourceWalletPage() {
  const { axiosAuth } = useAuth();
  const [documents, setDocuments] = useState(null);
  const [docTypes, setDocTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [newDoc, setNewDoc] = useState({
    doc_type: '',
    doc_name: '',
    description: '',
    expiration_date: '',
    document_number: '',
    issuing_authority: ''
  });
  const fileInputRef = useRef(null);
  const api = axiosAuth();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [docsRes, typesRes] = await Promise.all([
        api.get('/wallet/my-documents'),
        api.get('/wallet/document-types')
      ]);
      setDocuments(docsRes.data);
      setDocTypes(typesRes.data.document_types || []);
    } catch (err) {
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAddDocument = async () => {
    if (!newDoc.doc_type || !newDoc.doc_name) {
      toast.error('Please fill in required fields');
      return;
    }
    try {
      await api.post(`/wallet/add?doc_type=${newDoc.doc_type}&doc_name=${encodeURIComponent(newDoc.doc_name)}&description=${encodeURIComponent(newDoc.description)}&expiration_date=${newDoc.expiration_date || ''}&document_number=${encodeURIComponent(newDoc.document_number)}&issuing_authority=${encodeURIComponent(newDoc.issuing_authority)}`);
      toast.success('Document added to wallet!');
      setAddDialogOpen(false);
      setNewDoc({ doc_type: '', doc_name: '', description: '', expiration_date: '', document_number: '', issuing_authority: '' });
      fetchData();
    } catch (err) {
      toast.error('Failed to add document');
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!confirm('Are you sure you want to delete this document?')) return;
    try {
      await api.delete(`/wallet/document/${docId}`);
      toast.success('Document deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete document');
    }
  };

  const viewDocument = async (docId) => {
    try {
      const res = await api.get(`/wallet/document/${docId}`);
      setSelectedDoc(res.data);
      setViewDialogOpen(true);
    } catch (err) {
      toast.error('Failed to load document');
    }
  };

  const downloadDocument = () => {
    if (!selectedDoc?.file_data) return;
    
    const byteCharacters = atob(selectedDoc.file_data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: selectedDoc.file_type || 'application/octet-stream' });
    
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', selectedDoc.file_name || 'document');
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const getIcon = (iconName) => {
    return ICON_MAP[iconName] || FileText;
  };

  const getExpirationStatus = (date) => {
    if (!date) return null;
    const expDate = new Date(date);
    const now = new Date();
    const daysUntil = Math.ceil((expDate - now) / (1000 * 60 * 60 * 24));
    
    if (daysUntil < 0) return { status: 'expired', color: 'bg-red-100 text-red-700', text: 'Expired' };
    if (daysUntil < 30) return { status: 'expiring', color: 'bg-amber-100 text-amber-700', text: `Expires in ${daysUntil} days` };
    return { status: 'valid', color: 'bg-emerald-100 text-emerald-700', text: 'Valid' };
  };

  if (loading && !documents) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-violet-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="resource-wallet-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <Wallet className="text-violet-600" /> Resource Wallet
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Store and manage your important documents securely
          </p>
        </div>
        <Button onClick={() => setAddDialogOpen(true)} className="bg-violet-600 hover:bg-violet-700 text-white rounded-full gap-2">
          <Plus size={16} /> Add Document
        </Button>
      </div>

      {/* Stats */}
      {documents && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-0 shadow-sm rounded-2xl">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center">
                  <FileText className="text-violet-600" size={20} />
                </div>
              </div>
              <p className="text-2xl font-bold">{documents.total}</p>
              <p className="text-xs text-muted-foreground">Total Documents</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm rounded-2xl">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center">
                  <Shield className="text-sky-600" size={20} />
                </div>
              </div>
              <p className="text-2xl font-bold">{documents.by_type?.id_card || 0}</p>
              <p className="text-xs text-muted-foreground">ID Documents</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm rounded-2xl">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                  <Award className="text-emerald-600" size={20} />
                </div>
              </div>
              <p className="text-2xl font-bold">{documents.by_type?.certification || 0}</p>
              <p className="text-xs text-muted-foreground">Certifications</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm rounded-2xl">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                  <Briefcase className="text-amber-600" size={20} />
                </div>
              </div>
              <p className="text-2xl font-bold">{documents.by_type?.employment || 0}</p>
              <p className="text-xs text-muted-foreground">Employment Docs</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Info Banner */}
      <Card className="border-0 shadow-sm rounded-2xl bg-gradient-to-r from-violet-50 to-purple-50 border-l-4 border-l-violet-500">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-violet-600 shrink-0 mt-0.5" size={20} />
            <div>
              <p className="font-medium text-violet-900">Your Digital Document Wallet</p>
              <p className="text-sm text-violet-700 mt-1">
                Keep all your important documents in one place - IDs, certificates, employment records, and more. 
                Your documents are securely stored and accessible whenever you need them.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Document List */}
      {documents?.documents?.length === 0 ? (
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardContent className="p-8 text-center">
            <Wallet className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Your Wallet is Empty</h3>
            <p className="text-muted-foreground mb-4">
              Add your important documents to keep them safe and organized.
            </p>
            <Button onClick={() => setAddDialogOpen(true)} className="bg-violet-600 hover:bg-violet-700 text-white rounded-full gap-2">
              <Plus size={16} /> Add Your First Document
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {documents?.documents?.map(doc => {
            const typeInfo = doc.type_info || {};
            const IconComponent = getIcon(typeInfo.icon);
            const expStatus = getExpirationStatus(doc.expiration_date);
            return (
              <Card key={doc.id} className="border-0 shadow-sm rounded-2xl hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-violet-50 flex items-center justify-center shrink-0">
                      <IconComponent className="text-violet-600" size={24} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h3 className="font-semibold truncate">{doc.doc_name}</h3>
                          <Badge variant="outline" className="text-xs mt-1">{typeInfo.name || doc.doc_type}</Badge>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <Button variant="ghost" size="icon" onClick={() => viewDocument(doc.id)} className="h-8 w-8">
                            <Eye size={16} />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteDocument(doc.id)} className="h-8 w-8 text-red-500 hover:text-red-700">
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                      {doc.description && (
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{doc.description}</p>
                      )}
                      {doc.document_number && (
                        <p className="text-xs text-muted-foreground mt-1">Number: {doc.document_number}</p>
                      )}
                      <div className="flex items-center gap-3 mt-3">
                        <p className="text-xs text-muted-foreground">Added {formatDate(doc.uploaded_at)}</p>
                        {expStatus && (
                          <Badge className={expStatus.color}>{expStatus.text}</Badge>
                        )}
                        {doc.file_data && (
                          <Badge variant="outline" className="text-xs">Has File</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add Document Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Add Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Document Type *</label>
              <Select value={newDoc.doc_type} onValueChange={(v) => setNewDoc({ ...newDoc, doc_type: v })}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Select type..." />
                </SelectTrigger>
                <SelectContent>
                  {docTypes.map(type => (
                    <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Document Name *</label>
              <Input 
                value={newDoc.doc_name}
                onChange={(e) => setNewDoc({ ...newDoc, doc_name: e.target.value })}
                placeholder="e.g., Driver's License, OSHA Certificate"
                className="rounded-xl"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Document Number (optional)</label>
              <Input 
                value={newDoc.document_number}
                onChange={(e) => setNewDoc({ ...newDoc, document_number: e.target.value })}
                placeholder="License number, certificate ID, etc."
                className="rounded-xl"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Issuing Authority (optional)</label>
              <Input 
                value={newDoc.issuing_authority}
                onChange={(e) => setNewDoc({ ...newDoc, issuing_authority: e.target.value })}
                placeholder="e.g., State of Texas, OSHA"
                className="rounded-xl"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Expiration Date (optional)</label>
              <Input 
                type="date"
                value={newDoc.expiration_date}
                onChange={(e) => setNewDoc({ ...newDoc, expiration_date: e.target.value })}
                className="rounded-xl"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Notes (optional)</label>
              <DictationInput 
                value={newDoc.description}
                onChange={(e) => setNewDoc({ ...newDoc, description: e.target.value })}
                placeholder="Any additional notes..."
                className="rounded-xl"
                rows={2}
              showCleanup={false} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={handleAddDocument} className="bg-violet-600 hover:bg-violet-700 text-white rounded-full gap-2">
              <Plus size={16} /> Add Document
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Document Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="rounded-2xl max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Document Details</DialogTitle>
          </DialogHeader>
          {selectedDoc && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-violet-50 flex items-center justify-center">
                  {(() => {
                    const IconComponent = getIcon(selectedDoc.type_info?.icon);
                    return <IconComponent className="text-violet-600" size={28} />;
                  })()}
                </div>
                <div>
                  <h3 className="font-semibold text-lg">{selectedDoc.doc_name}</h3>
                  <Badge variant="outline">{selectedDoc.type_info?.name || selectedDoc.doc_type}</Badge>
                </div>
              </div>
              
              <div className="space-y-2">
                {selectedDoc.document_number && (
                  <div className="flex justify-between p-2 bg-muted/30 rounded-lg text-sm">
                    <span className="text-muted-foreground">Document Number</span>
                    <span className="font-medium">{selectedDoc.document_number}</span>
                  </div>
                )}
                {selectedDoc.issuing_authority && (
                  <div className="flex justify-between p-2 bg-muted/30 rounded-lg text-sm">
                    <span className="text-muted-foreground">Issuing Authority</span>
                    <span className="font-medium">{selectedDoc.issuing_authority}</span>
                  </div>
                )}
                {selectedDoc.expiration_date && (
                  <div className="flex justify-between p-2 bg-muted/30 rounded-lg text-sm">
                    <span className="text-muted-foreground">Expiration Date</span>
                    <span className="font-medium">{selectedDoc.expiration_date}</span>
                  </div>
                )}
                <div className="flex justify-between p-2 bg-muted/30 rounded-lg text-sm">
                  <span className="text-muted-foreground">Added</span>
                  <span className="font-medium">{formatDate(selectedDoc.uploaded_at)}</span>
                </div>
              </div>

              {selectedDoc.description && (
                <div className="p-3 bg-muted/30 rounded-xl">
                  <p className="text-sm font-medium mb-1">Notes</p>
                  <p className="text-sm text-muted-foreground">{selectedDoc.description}</p>
                </div>
              )}

              {selectedDoc.file_data && (
                <Button onClick={downloadDocument} variant="outline" className="w-full rounded-full gap-2">
                  <Download size={16} /> Download File
                </Button>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)} className="rounded-full">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
